import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { 
  MoreHorizontal, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Edit,
  UserPlus,
  Settings
} from 'lucide-react';
import { StaffMember } from '@/services/staffService';
import InviteStatusBadge from '@/components/families/InviteStatusBadge';
import { InviteStatus } from '@/types/constants';

interface StaffTableRowProps {
  staff: StaffMember;
  classAssignments: number;
  assignedLocationsCount: number;
  onUpdate: () => void;
  onStatusChange: (staffId: string, isActive: boolean) => void;
  onEdit: (staff: StaffMember) => void;
  onSendInvite: (staff: StaffMember) => void;
  onInviteWithoutEmail: (staff: StaffMember) => void;
  onManageLocations: (staff: StaffMember) => void;
}

const getRoleBadgeVariant = (role?: string): "default" | "outline" | "secondary" | "destructive" => {
  switch (role) {
    case "admin":
      return "destructive";
    case "instructor":
      return "default";
    case "manager":
      return "secondary";
    default:
      return "outline";
  }
};

const getInviteStatus = (staff: StaffMember): InviteStatus => {
  if (staff.user_id) {
    return 'accepted'; // Has user_id = registered
  } else if (staff.email) {
    return 'sent'; // Has email but no user_id = invited
  } else {
    return 'pending'; // No email = not invited yet
  }
};

const StaffTableRow: React.FC<StaffTableRowProps> = ({
  staff,
  classAssignments,
  assignedLocationsCount,
  onUpdate,
  onStatusChange,
  onEdit,
  onSendInvite,
  onInviteWithoutEmail,
  onManageLocations
}) => {
  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || 'U';
    const last = lastName?.charAt(0) || 'N';
    return `${first}${last}`.toUpperCase();
  };

  const handleRowClick = (e: React.MouseEvent) => {
    // Don't trigger row click if clicking on interactive elements
    if ((e.target as HTMLElement).closest('button, [role="button"], .switch')) {
      return;
    }
    onEdit(staff);
  };

  return (
    <>
      {/* Staff Info */}
      <td className="px-4 py-4 hover:bg-muted/50 transition-colors cursor-pointer" onClick={handleRowClick}>
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10 shrink-0">
            <AvatarImage src={staff.profile_photo_url || ''} alt={`${staff.first_name} ${staff.last_name}`} />
            <AvatarFallback className="bg-gradient-to-br from-blue-100 to-purple-100 text-blue-700">
              {getInitials(staff.first_name, staff.last_name)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="font-medium truncate hover:text-primary transition-colors">
              {staff.first_name || 'Unknown'} {staff.last_name || 'User'}
            </div>
            <div className="text-sm text-muted-foreground truncate">
              {staff.email || 'No email'}
            </div>
          </div>
        </div>
      </td>

      {/* Role & Invite Status */}
      <td className="px-4 py-4 hover:bg-muted/50 transition-colors cursor-pointer" onClick={handleRowClick}>
        <div className="space-y-2">
          <Badge variant={getRoleBadgeVariant(staff.role)} className="text-xs">
            {staff.role || 'Staff'}
          </Badge>
          <InviteStatusBadge status={getInviteStatus(staff)} />
        </div>
      </td>

      {/* Contact Info */}
      <td className="px-4 py-4 hover:bg-muted/50 transition-colors cursor-pointer" onClick={handleRowClick}>
        <div className="space-y-1 text-sm">
          {staff.email && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Mail className="h-3 w-3" />
              <span className="truncate text-xs">{staff.email}</span>
            </div>
          )}
          {staff.phone && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Phone className="h-3 w-3" />
              <span className="text-xs">{staff.phone}</span>
            </div>
          )}
        </div>
      </td>

      {/* Assignments */}
      <td className="px-4 py-4 hover:bg-muted/50 transition-colors cursor-pointer" onClick={handleRowClick}>
        <div className="space-y-1 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="h-3 w-3" />
            <span className="text-xs">
              {assignedLocationsCount} {assignedLocationsCount === 1 ? 'location' : 'locations'}
            </span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span className="text-xs">
              {classAssignments} {classAssignments === 1 ? 'class' : 'classes'}
            </span>
          </div>
        </div>
      </td>

      {/* Status */}
      <td className="px-4 py-4 hover:bg-muted/50 transition-colors">
        <div className="flex items-center gap-2">
          <Switch
            checked={staff.is_active}
            onCheckedChange={(checked) => onStatusChange(staff.id, checked)}
            className="switch"
          />
          <span className={`text-xs ${staff.is_active ? 'text-green-600' : 'text-muted-foreground'}`}>
            {staff.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
      </td>

      {/* Actions */}
      <td className="px-4 py-4 hover:bg-muted/50 transition-colors">
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => onEdit(staff)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit Details
              </DropdownMenuItem>
              
              <DropdownMenuItem onClick={() => onManageLocations(staff)}>
                <MapPin className="h-4 w-4 mr-2" />
                Manage Locations
              </DropdownMenuItem>
              
              {/* Invite to Platform (no email = not invited yet) */}
              {!staff.email && (
                <DropdownMenuItem onClick={() => onInviteWithoutEmail(staff)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Invite to Platform
                </DropdownMenuItem>
              )}
              
              {/* Resend Invite (has email but no user_id = already invited) */}
              {staff.email && !staff.user_id && (
                <DropdownMenuItem onClick={() => onSendInvite(staff)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Resend Invite
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </>
  );
};

export default StaffTableRow;